import { useCallback, useEffect, useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { ShoppingCartItem } from '@obeta/models/lib/models'
import { useReplacementProducts } from './useReplacementProducts'

type ReplacementVariant = 'DISCONTINUED_ITEM_WITHOUT_STOCK' | 'DISCONTINUED_ITEM_WITH_PARTIAL_STOCK'

type UseReplacementDialogOptions = {
  itemOfChoice: ShoppingCartItem
}

export type ReplacementAmountItem = { sapId: string; amount: number }
type UseReplacementDialogResult = {
  title: string
  description: string
  disableItemOfChoice: boolean
  itemOfChoiceAmount: number
  replacementAmounts: Array<ReplacementAmountItem>
  changeReplacementItemAmount: (sapId: string, amount: number) => void
  replacementItems: ShoppingCartItem[]
  setReplacementItems: (replacementItems: ShoppingCartItem[]) => void
  setShouldFetchReplacementItems: (shouldFetchReplacementItems: boolean) => void
}

type UseReplacementDialog = (options: UseReplacementDialogOptions) => UseReplacementDialogResult

const getReplacementVariant = (itemOfChoice: ShoppingCartItem): ReplacementVariant => {
  if (!itemOfChoice || !itemOfChoice.product) {
    throw new Error('Invalid ShoppingCartItem provided to getReplacementVariant')
  }

  const { isDiscontinued } = itemOfChoice.product
  const totalStock = getTotalAmountInStock(itemOfChoice)

  if (!isDiscontinued) {
    // TODO: Add a new variant type such as "NOT_DISCONTINUED" if needed
    // For now, fallback to 'DISCONTINUED_ITEM_WITHOUT_STOCK'
    return 'DISCONTINUED_ITEM_WITHOUT_STOCK'
  }

  if (totalStock > 0) {
    return 'DISCONTINUED_ITEM_WITH_PARTIAL_STOCK'
  }

  return 'DISCONTINUED_ITEM_WITHOUT_STOCK'
}

export const getTotalAmountInStock = (shoppingCartItem: ShoppingCartItem): number => {
  if (!shoppingCartItem || !shoppingCartItem.product) return 0
  const stock = shoppingCartItem.product.stock
  if (!Array.isArray(stock)) return 0
  // Edge case handling for only online or only store stock
  if (stock.length === 1) return stock[0]?.amount ?? 0
  // Most common case - adds up all amounts
  if (stock.length === 2) return (stock[0]?.amount ?? 0) + (stock[1]?.amount ?? 0)
  // Fallback for more than 0 or more than 2 stock items (unlikely, but safe)
  return stock.reduce(
    (accumulatedStock, individualStock) => accumulatedStock + (individualStock?.amount ?? 0),
    0
  )
}

export const isStockSufficient = (desiredAmount: number, totalAmountStocked: number): boolean => {
  return desiredAmount <= totalAmountStocked
}

const calculateReplacementAmounts = (
  itemOfChoice: ShoppingCartItem,
  replacementItems: ShoppingCartItem[],
  itemOfChoiceAmount = 0,
  overrides: Record<string, number> = {}
): ReplacementAmountItem[] => {
  if (!Array.isArray(replacementItems) || replacementItems.length === 0) return []
  if (!itemOfChoice) return []

  return replacementItems.map((item: ShoppingCartItem, idx: number) => {
    if (!item || !item.sapId || !item.product) {
      return { sapId: '', amount: 0 }
    }
    const overrideAmount = overrides[item.sapId]
    // Use user-chosen amount if available, otherwise use the originally requested amount for the first replacement item - for all other items, default to 0
    // TODO What are the actual Ecom requirements for additional replacement items? Confirm with Maxi
    const requestedAmount =
      overrideAmount !== undefined
        ? overrideAmount
        : idx === 0
        ? // Replace difference between originally requested amount and purchasable/chosen amount
          itemOfChoice.amount - itemOfChoiceAmount //itemOfChoice.amount = originally requested amount; itemOfChoiceAmount = remaining purchasable amount (stock-wise) or user-chosen amount (might be 0)
        : 0
    const totalInStock = getTotalAmountInStock(item)

    // Don't assign more than the available stock for discontinued items
    // If possible, however, use the requested amount (i.e.user-chosen amount if available, otherwise the originally requested amount)
    const amountToSet =
      item.product.isDiscontinued && !isStockSufficient(itemOfChoice.amount, totalInStock)
        ? totalInStock
        : requestedAmount

    return { sapId: item.sapId, amount: amountToSet }
  })
}

export const useReplacementDialog: UseReplacementDialog = ({ itemOfChoice }) => {
  const [replacementItems, setReplacementItems] = useState<ShoppingCartItem[]>([])

  // An object mapping sapIds to a user-chosen (via input) amount for specific replacement items
  const [replacementAmountsOverrides, setReplacementAmountsOverrides] = useState<
    Record<string, number>
  >({})
  const [itemOfChoiceAmount, setItemOfChoiceAmount] = useState(0)
  const [shouldFetchReplacementItems, setShouldFetchReplacementItems] = useState<boolean>(true)

  const { t } = useTranslation()
  const { loadReplacementProductsAsShoppingCartItems } = useReplacementProducts()

  // Derived state
  const replacementVariant = getReplacementVariant(itemOfChoice)
  const isDiscontinuedItemWithoutStockVariant =
    replacementVariant === 'DISCONTINUED_ITEM_WITHOUT_STOCK'

  const replacementAmounts = useMemo(
    () =>
      calculateReplacementAmounts(
        itemOfChoice,
        replacementItems,
        itemOfChoiceAmount,
        replacementAmountsOverrides
      ),
    [itemOfChoice, replacementItems, itemOfChoiceAmount, replacementAmountsOverrides]
  )

  // Load and set replacement products
  const loadReplacementProducts = useCallback(async (): Promise<void> => {
    if (!shouldFetchReplacementItems) return
    if (!itemOfChoice || !itemOfChoice.sapId || !itemOfChoice.cartId) return
    const result = await loadReplacementProductsAsShoppingCartItems(
      itemOfChoice.sapId,
      itemOfChoice.cartId as string
    )

    if (!result) {
      setReplacementItems([])
      return
    }

    setReplacementItems(result)
  }, [shouldFetchReplacementItems, itemOfChoice, loadReplacementProductsAsShoppingCartItems])

  // Reset fetch flag when item changes
  useEffect(() => {
    if (!itemOfChoice || !itemOfChoice.product) return
    setShouldFetchReplacementItems(true)
  }, [itemOfChoice, itemOfChoice.product.sapId])

  // Fetch replacements when needed
  useEffect(() => {
    if (!itemOfChoice || !itemOfChoice.product) return
    loadReplacementProducts()
  }, [loadReplacementProducts, itemOfChoice])

  // Calculate itemOfChoiceAmount when variant or item changes
  useEffect(() => {
    if (!itemOfChoice || !itemOfChoice.product) return
    const maxInStock = getTotalAmountInStock(itemOfChoice)
    // Note that user can only select (and respectively update) amounts for itemsOfChoice that are discontinued (as we are trying to sell remaining stock)
    const amount = itemOfChoice.product.isDiscontinued
      ? isStockSufficient(itemOfChoice.amount, maxInStock)
        ? itemOfChoice.amount
        : maxInStock
      : 0
    setItemOfChoiceAmount(isDiscontinuedItemWithoutStockVariant ? 0 : amount)
  }, [isDiscontinuedItemWithoutStockVariant, itemOfChoice])

  // Change replacement item amount to the one that is requested by the user - treated here as an "override"
  const changeReplacementItemAmount = useCallback((sapId: string, amount: number): void => {
    if (!sapId) return
    setReplacementAmountsOverrides((prev: Record<string, number>) => ({ ...prev, [sapId]: amount }))
  }, [])

  return {
    title: t(`REPLACEMENT.DIALOG.${replacementVariant}.TITLE`),
    description: t(`REPLACEMENT.DIALOG.${replacementVariant}.DESCRIPTION`, {
      count: itemOfChoice.amount,
      unit: itemOfChoice.product.unit,
    }),
    disableItemOfChoice: isDiscontinuedItemWithoutStockVariant, // TODO What about a not for sale item?
    itemOfChoiceAmount,
    replacementAmounts,
    changeReplacementItemAmount,
    replacementItems,
    setReplacementItems,
    setShouldFetchReplacementItems,
  }
}
